import ToolsPage from "./pages/Tools/ToolsPage";

import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { OrganizationProvider } from "./contexts/OrganizationContext";
import { useVisualViewport } from "./hooks/useVisualViewport";
import ThemeProvider from "./components/Theme/ThemeProvider";
import { Layout } from "./components/Layout/Layout";
import LoginForm from "./components/Auth/LoginForm";
import RegisterForm from "./components/Auth/RegisterForm";
import Dashboard from "./pages/Dashboard/Dashboard";
import BriefingDashboard from "./pages/Briefing/BriefingDashboard";
import StartBriefing from "./pages/Briefing/StartBriefing";
import StartMonitoring from "./pages/Monitoring/StartMonitoring";
import StartPricing from "./pages/Admin/StartPricing";
import AuthContinue from "./pages/Briefing/AuthContinue";
import SimilarityDashboard from "./pages/Similarity/SimilarityDashboard";
import SimilarityTab from "./pages/SimilarityTab";
import ClusteringDashboard from "./pages/Clustering/ClusteringDashboard";
import { CompetitorAnalysisDashboard } from "./pages/CompetitorAnalysis/CompetitorAnalysisDashboard";
import MonitoringDashboard from "./pages/Monitoring/MonitoringDashboard";
import MonitoringDetail from "./pages/MonitoringDetail";
import SnapshotsTab from "./pages/SnapshotsTab";
import ChangesTab from "./pages/ChangesTab";
import SettingsTab from "./pages/SettingsTab";
import URLHistoryPage from "./pages/URLHistory/URLHistoryPage";
import MonitoringDebug from "./pages/MonitoringDebug";
import AnalyticsWebsites from "./pages/Analytics/AnalyticsWebsites";
import AnalyticsWorkspace from "./pages/Analytics/AnalyticsWorkspace";
import LegacyAnalyticsRedirect from "./pages/Analytics/LegacyAnalyticsRedirect";
import AnalyticsOAuthCallback from "./pages/Analytics/AnalyticsOAuthCallback";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import {
  OrganizationsPage,
  UsersPage,
  PermissionsPage,
  SessionsPage,
  BillingPage,
  AuditPage,
} from "./pages/Admin/AdminPages";
import TeamMembers from "./pages/Admin/TeamMembers";
import { UpgradePlan } from "./pages/Admin/UpgradePlan";
import { UserProfileSettings } from "./components/UserProfile";
import Profile from "./pages/Profile/Profile";
import Settings from "./pages/Settings/Settings";
import AccessTokensPage from "./pages/Settings/AccessTokensPage";
import SecurityPage from "./pages/Settings/SecurityPage";
import AcceptInvitation from "./pages/AcceptInvitation/AcceptInvitation";
import VerifyEmail from "./pages/VerifyEmail/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import GoogleCallback from "./pages/GoogleCallback/GoogleCallback";
import GitHubSetupCallback from "./pages/GitHubSetup/GitHubSetupCallback";
import ToastProvider from "./components/Toast/ToastProvider";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import ConsentBanner from "./components/ConsentBanner/ConsentBanner";
import RequireAuth from "./components/Auth/RequireAuth";
import LegalAcknowledgmentGate from "./components/LegalAcknowledgmentGate/LegalAcknowledgmentGate";
import PaymentStatusBanner from "./components/Billing/PaymentStatusBanner/PaymentStatusBanner";
import RequirePermission from "./components/RequirePermission/RequirePermission";
import { Permission } from "./hooks/usePermissions";
import "./globals.css";
import { WordCloudPage } from "./pages/WordCloud/WordCloudPage";
import { BillingSuccess } from "./pages/Admin/BillingSuccess";
import { BillingCancel } from "./pages/Admin/BillingCancel";
import AgentDashboard from "./pages/Agent/AgentDashboard";
import GoalDetailPage from "./pages/Agent/GoalDetailPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes - increased from 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes - same as before
      retry: (failureCount, error) => {
        // Don't retry on 401/403 (auth) or 429 (quota — retrying just wastes
        // the user's remaining budget and amplifies the error).
        const status = (error as { status?: number } | null | undefined)
          ?.status;
        if (status === 401 || status === 403 || status === 429) {
          return false;
        }
        // Retry up to 2 times for other errors
        return failureCount < 2;
      },

      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false, // Disable automatic refetch on window focus
      refetchOnMount: true, // Refetch on component mount if data is stale
      refetchOnReconnect: true, // Refetch when reconnecting to the internet
    },
    mutations: {
      retry: 1, // Retry mutations once on failure
      retryDelay: 1000,
    },
  },
});

function AppContent() {
  // Create router inside the component so it has access to context
  const router = createBrowserRouter([
    // Public routes (no authentication required)
    {
      path: "/login",
      element: <LoginForm />,
    },
    {
      path: "/register",
      element: <RegisterForm />,
    },
    {
      path: "/start/briefing",
      element: <StartBriefing />,
    },
    {
      path: "/start/pricing",
      element: <StartPricing />,
    },
    {
      path: "/start/monitoring",
      element: <StartMonitoring />,
    },
    {
      path: "/accept-invitation",
      element: <AcceptInvitation />,
    },
    {
      // Alternative URL pattern for invitation links
      path: "/invite/:token",
      element: <AcceptInvitation />,
    },
    {
      path: "/verify-email",
      element: <VerifyEmail />,
    },
    {
      path: "/forgot-password",
      element: <ForgotPassword />,
    },
    {
      path: "/reset-password",
      element: <ResetPassword />,
    },
    {
      path: "/auth/google/callback",
      element: <GoogleCallback />,
    },
    {
      // GitHub App install return page (Spec 04 one-click git connect).
      // Public like the Google callback: it only relays query params back
      // to the opener window / localStorage — the claim itself is an
      // authenticated API call made by IntegrationsSettings.
      path: "/integrations/github/setup",
      element: <GitHubSetupCallback />,
    },

    // Protected routes (authentication required)
    {
      path: "/",
      element: (
        <RequireAuth>
          <ErrorBoundary>
            {/* Legal Acknowledgment Gate renders inside RequireAuth so it only
                appears for authenticated users. It self-hides when the
                server-computed legal_status is all_ok. */}
            <LegalAcknowledgmentGate />
            {/* Persistent notice when the subscription is not paid up. Self-
                hides unless status is past_due or unpaid. */}
            <PaymentStatusBanner />
            <Layout />
          </ErrorBoundary>
        </RequireAuth>
      ),
      children: [
        {
          index: true,
          element: <Navigate to="/dashboard" replace />,
        },
        {
          path: "tools",
          element: <ToolsPage />,
        },
        {
          path: "dashboard",
          element: <Dashboard />,
        },
        {
          path: "auth/continue",
          element: <AuthContinue />,
        },
        {
          path: "agent",
          element: <AgentDashboard />,
        },
        {
          path: "agent/goals/:id",
          element: <GoalDetailPage />,
        },

        // Briefing routes
        {
          path: "briefings",
          children: [
            {
              index: true,
              element: <BriefingDashboard />,
            },
            {
              path: "new",
              element: <Navigate to="/briefings?create=1" replace />,
            },
            {
              path: ":projectId",
              element: <BriefingDashboard />,
            },
          ],
        },

        // Similarity routes
        {
          path: "similarity",
          children: [
            {
              index: true,
              element: <SimilarityDashboard />,
            },
            {
              path: ":projectId",
              element: <SimilarityDashboard />,
              children: [
                {
                  index: true,
                  element: <SimilarityDashboard />,
                },
                {
                  path: "create",
                  element: <SimilarityDashboard />,
                },
                {
                  path: "jobs/:jobId",
                  element: <SimilarityDashboard />,
                },
              ],
            },
            {
              path: "legacy",
              element: <SimilarityTab />,
            },
          ],
        },

        // Competitor Analysis routes
        {
          path: "competitor-analysis",
          children: [
            {
              index: true,
              element: <CompetitorAnalysisDashboard />,
            },
            {
              path: ":projectId",
              element: <CompetitorAnalysisDashboard />,
            },
            {
              path: ":projectId/jobs/:jobId",
              element: <CompetitorAnalysisDashboard />,
            },
          ],
        },

        // Clustering routes
        {
          path: "clustering",
          children: [
            {
              index: true,
              element: <ClusteringDashboard />,
            },
            {
              path: ":projectId",
              element: <ClusteringDashboard />,
            },
            {
              path: ":projectId/create",
              element: <ClusteringDashboard />,
            },
            {
              path: ":projectId/batches/:batchId",
              element: <ClusteringDashboard />,
            },
          ],
        },

        // Monitoring routes
        {
          path: "monitoring",
          children: [
            {
              index: true,
              element: <MonitoringDashboard />,
            },
            {
              path: ":id",
              element: <MonitoringDetail />,
              children: [
                {
                  index: true,
                  element: <SnapshotsTab />,
                },
                {
                  path: "changes",
                  element: <ChangesTab />,
                },
                {
                  path: "settings",
                  element: <SettingsTab />,
                },
              ],
            },
            {
              path: ":id/url-history",
              element: <URLHistoryPage />,
            },
          ],
        },

        // Debug route — dev builds only. In prod the entry is omitted so the
        // page cannot be reached even by URL. (Hygiene #13.)
        ...(import.meta.env.DEV
          ? [{ path: "debug", element: <MonitoringDebug /> }]
          : []),

        // Analytics routes
        {
          path: "analytics",
          children: [
            {
              index: true,
              element: <AnalyticsWebsites />,
            },
            {
              path: "oauth/callback",
              element: <AnalyticsOAuthCallback />,
            },
            {
              path: "websites/:websiteId/:tab?",
              element: <AnalyticsWorkspace />,
            },
            {
              path: "site/:id",
              element: <LegacyAnalyticsRedirect kind="site" />,
            },
            {
              path: "ga4/:id",
              element: <LegacyAnalyticsRedirect kind="ga4" />,
            },
            {
              path: "gsc/:id",
              element: <LegacyAnalyticsRedirect kind="gsc" />,
            },
          ],
        },
        // Word Cloud route
        {
          path: "wordcloud",
          children: [
            {
              index: true,
              element: <WordCloudPage />,
            },
          ],
        },

        // Admin routes.
        // Gate the entire /admin tree behind ORG_INVITE (admin / owner only).
        // This mirrors the role check in `TopBar/UserMenu` that hides the
        // Admin entry and prevents direct URL access (`/admin`,
        // `/admin/billing`, etc.) for viewers and members. Stricter child
        // guards (e.g. ORG_VIEW_AUDIT_LOG on `/admin/audit`) remain as
        // defense-in-depth.
        {
          path: "admin",
          element: (
            <RequirePermission permission={Permission.ORG_INVITE}>
              <Outlet />
            </RequirePermission>
          ),
          children: [
            {
              index: true,
              element: <AdminDashboard />,
            },
            {
              path: "organizations",
              element: <OrganizationsPage />,
            },
            {
              path: "users",
              element: <UsersPage />,
            },
            {
              path: "members",
              element: (
                <RequirePermission permission={Permission.ORG_INVITE}>
                  <TeamMembers />
                </RequirePermission>
              ),
            },
            {
              path: "permissions",
              element: <PermissionsPage />,
            },
            {
              path: "sessions",
              element: <SessionsPage />,
            },
            {
              path: "billing",
              element: <BillingPage />,
            },
            {
              path: "billing/upgrade",
              element: <UpgradePlan />,
            },
            {
              path: "billing/success",
              element: <BillingSuccess />,
            },
            {
              path: "billing/cancel",
              element: <BillingCancel />,
            },
            {
              path: "audit",
              element: (
                <RequirePermission permission={Permission.ORG_VIEW_AUDIT_LOG}>
                  <AuditPage />
                </RequirePermission>
              ),
            },
          ],
        },

        // Profile route
        {
          path: "profile",
          element: <Profile />,
          errorElement: (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "50vh",
                textAlign: "center",
                padding: "2rem",
              }}
            >
              <h2 style={{ color: "var(--color-error)", marginBottom: "1rem" }}>
                Profile Error
              </h2>
              <p
                style={{
                  color: "var(--color-text-secondary)",
                  marginBottom: "2rem",
                }}
              >
                There was an error loading your profile. Please try refreshing
                the page.
              </p>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: "0.75rem 1.5rem",
                  background: "var(--color-primary)",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Refresh Page
              </button>
            </div>
          ),
        },

        // Settings routes
        {
          path: "settings",
          children: [
            {
              index: true,
              element: <Settings />,
            },
            {
              path: "profile",
              element: <UserProfileSettings />,
            },
            {
              path: "billing",
              element: (
                <div style={{ padding: "2rem" }}>
                  Billing Settings (Coming Soon)
                </div>
              ),
            },
            {
              path: "notifications",
              element: (
                <div style={{ padding: "2rem" }}>
                  Notification Settings (Coming Soon)
                </div>
              ),
            },
            {
              path: "tokens",
              element: <AccessTokensPage />,
            },
            {
              path: "security",
              element: <SecurityPage />,
            },
          ],
        },
      ],
    },

    // 404 catch-all
    {
      path: "*",
      element: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            textAlign: "center",
            padding: "2rem",
          }}
        >
          <h1 style={{ fontSize: "4rem", margin: "0" }}>404</h1>
          <h2 style={{ margin: "1rem 0" }}>Page Not Found</h2>
          <p
            style={{
              color: "var(--color-text-secondary)",
              marginBottom: "2rem",
            }}
          >
            The page you're looking for doesn't exist.
          </p>
          <a
            href="/dashboard"
            style={{
              padding: "0.75rem 1.5rem",
              background: "var(--color-primary)",
              color: "white",
              textDecoration: "none",
              borderRadius: "6px",
            }}
          >
            Go to Dashboard
          </a>
        </div>
      ),
    },
  ]);

  return <RouterProvider router={router} />;
}

// P0.3 / P1.8 — the global reporter is installed by `initMonitoring()` in
// main.tsx. ErrorBoundary.componentDidCatch, logger.warn/error, and the
// window-level handlers all funnel through `window.reportError`.

function GlobalConsentBanner() {
  const { user } = useAuth();
  return <ConsentBanner authenticated={!!user} />;
}

function App() {
  useVisualViewport();
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider>
              <OrganizationProvider>
                <AppContent />
                {/* P1.6 — GDPR consent banner. Mounted outside RequireAuth so it
                    appears on the public login/register surface as well. */}
                <GlobalConsentBanner />
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </OrganizationProvider>
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
